import React, { useState, useEffect } from 'react';
import { Percent, Ticket, Wallet, TrendingDown } from 'lucide-react';

/**
 * Ícone animado da marca SuperVitrine: um quadrado que alterna cinco símbolos
 * ("S", %, cupom, carteira e queda de preço) a cada 2,8 s, com flutuação leve.
 *
 * As cores vêm dos tokens de marca (`--sv-brand-icon-*` em index.css), iguais em
 * todos os quadros: o fundo e o símbolo não mudam de cor durante a animação.
 * O movimento e o tempo são os mesmos da versão anterior.
 */
const ICONS = [
  { Icon: null, text: 'S' },
  { Icon: Percent },
  { Icon: Ticket },
  { Icon: Wallet },
  { Icon: TrendingDown },
];

const FUNDO = 'var(--sv-brand-icon-bg, linear-gradient(135deg, #3D8BFF 0%, #1756B8 100%))';
const SIMBOLO = 'var(--sv-brand-icon-fg, #FFFFFF)';

export default function AnimatedBrandIcon({ size = 40, animate = true, glow = false, className = '' }) {
  const [idx, setIdx] = useState(0);
  const [show, setShow] = useState(true);
  const iconSize = Math.round(size * 0.44);
  const borderRadius = Math.round(size * 0.25);

  useEffect(() => {
    if (!animate) return undefined;
    let troca;
    const cycle = setInterval(() => {
      setShow(false);
      troca = setTimeout(() => { setIdx(i => (i + 1) % ICONS.length); setShow(true); }, 300);
    }, 2800);
    return () => { clearInterval(cycle); clearTimeout(troca); };
  }, [animate]);

  const { Icon, text } = ICONS[animate ? idx : 0];

  return (
    <>
      <style>{`
        @keyframes sv-float{0%,100%{transform:translateY(0)}50%{transform:translateY(-2px)}}
        @keyframes sv-glow-pulse{0%,100%{box-shadow:0 0 30px rgba(var(--sv-brand-glow-rgb, 61, 139, 255),.25), 0 0 60px rgba(var(--sv-brand-glow-rgb, 61, 139, 255),.1)}50%{box-shadow:0 0 45px rgba(var(--sv-brand-glow-rgb, 61, 139, 255),.35), 0 0 90px rgba(var(--sv-brand-glow-rgb, 61, 139, 255),.15)}}
        @keyframes sv-wave-pulse{
          0%{transform:scale(1) rotate(0deg);opacity:0.15;}
          100%{transform:scale(8) rotate(180deg);opacity:0;}
        }
      `}</style>
      <div className={`relative inline-flex items-center justify-center shrink-0 ${className}`}
        data-testid="brand-icon"
        style={{
          width: size, height: size, borderRadius,
          animation: animate ? 'sv-float 4s ease-in-out infinite' : undefined,
        }}>
        {/* Ondas quadradas - muito lentas e suaves */}
        {glow && [0, 1, 2, 3].map(i => (
          <div key={i} className="absolute" style={{
            inset: 0,
            border: '1px solid rgba(var(--sv-brand-glow-rgb, 61, 139, 255), 0.18)',
            borderRadius: borderRadius,
            animation: `sv-wave-pulse 20s linear ${i * 5}s infinite`,
            pointerEvents: 'none',
          }} />
        ))}
        {/* Quadrado da marca */}
        <div className="relative inline-flex items-center justify-center overflow-hidden"
          style={{
            width: size, height: size, borderRadius,
            background: FUNDO,
            ...(glow ? { animation: 'sv-glow-pulse 3s ease-in-out infinite' } : {}),
          }}>
        <div style={{
          transition: 'opacity .3s, transform .3s',
          opacity: show ? 1 : 0,
          transform: show ? 'scale(1) translateY(0)' : 'scale(0.5) translateY(4px)',
        }}>
          {Icon
            ? <Icon size={iconSize} strokeWidth={2} style={{ color: SIMBOLO }} />
            : <span style={{ fontSize: iconSize * 1.1, fontWeight: 800, color: SIMBOLO, fontFamily: "var(--font-display)", lineHeight: 1 }}>{text}</span>
          }
        </div>
        </div>
      </div>
    </>
  );
}
